/**
 * « Installer Ridalo » — what to offer in the menu, decided from the browser and the device (Lot PWA 5).
 *
 * Pure logic, no UI, no side effect, NEVER triggers anything by itself: nothing here opens the browser's install
 * dialog, a panel or the notification permission. The menu entry calls `promptInstall()` / opens its panel only
 * when the person taps it. It reuses the capture of the native prompt and the « installed » marker of lib/pwaInstall.js.
 *
 * What can really be known (and what cannot):
 *  - Running as an installed app is detected (`isStandalone`: iOS `navigator.standalone`, display-mode media
 *    queries) and remembered (`state.installed`, set on `appinstalled`, on an accepted install and while running
 *    installed). A browser TAB cannot tell, in general, that Ridalo was installed earlier from another door, so the
 *    manual instructions always say « if Ridalo is already on your home screen, open it from its icon ».
 *  - Android / Chrome-like browsers: `beforeinstallprompt` gives the native dialog (only when the browser decides
 *    the site is installable and not yet installed).
 *  - iPhone / iPad: no such event. Safari: Share → « Add to Home Screen ». Chrome for iOS can do it too since
 *    iOS 16.4 (Share / menu → « Add to Home Screen »). Edge, Firefox and the others: not confirmed for every version,
 *    so the wording stays prudent and never says that Safari is mandatory — it only suggests it as the way out.
 *  - In-app browsers (social networks, messengers, WebViews) cannot install: the person is told to open Ridalo in a browser.
 * Phones and tablets only: a desktop browser gets no entry.
 */
import { getDeferredPrompt, isInAppBrowser, isIos, isStandalone, readState } from './pwaInstall';

export const iosVersion = (ua = '') => {
  const m = /OS (\d+)[_.](\d+)/.exec(ua);
  return m ? { major: Number(m[1]), minor: Number(m[2]) } : null;
};

/** 'safari' | 'chrome' | 'edge' | 'firefox' | 'opera' | 'duckduckgo' (every iOS browser is WebKit; the token tells which). */
export const iosBrowser = (ua = '') => {
  if (/CriOS/.test(ua)) return 'chrome';
  if (/EdgiOS/.test(ua)) return 'edge';
  if (/FxiOS/.test(ua)) return 'firefox';
  if (/OPiOS|OPT\//.test(ua)) return 'opera';
  if (/DuckDuckGo/.test(ua)) return 'duckduckgo';
  return 'safari';
};

export const isAndroid = (ua = '') => /Android/i.test(ua);
export const isAndroidWebView = (ua = '') => /; wv\)/.test(ua);

/** iOS 16.4 added « Add to Home Screen » for browsers other than Safari. An unknown version is given the benefit of the doubt. */
export const thirdPartyBrowsersCanAdd = (version) => !version || version.major > 16 || (version.major === 16 && version.minor >= 4);

/**
 * { kind } or null.
 *  native           Android (Chrome-like): the browser handed us its install dialog
 *  android-manual   Android without that dialog: menu of the browser
 *  ios-safari | ios-chrome | ios-edge | ios-other   iPhone / iPad, per browser
 *  ios-old          iOS < 16.4 in a browser other than Safari: open Safari / update
 *  open-in-browser  in-app browser or WebView: cannot install, open a real browser
 *  null             nothing to offer: already installed / running installed, or not a phone or tablet
 */
export const installOption = ({
  nav = typeof navigator !== 'undefined' ? navigator : { userAgent: '' },
  standalone = isStandalone(),
  state = readState(),
  hasNativePrompt = Boolean(getDeferredPrompt()),
} = {}) => {
  const ua = nav.userAgent || '';
  const ios = isIos(nav);
  const android = isAndroid(ua);
  if (!ios && !android) return null;
  if (standalone || (state && state.installed)) return null;
  if (hasNativePrompt && !ios) return { kind: 'native' };
  if (isInAppBrowser(nav) || (android && isAndroidWebView(ua))) return { kind: 'open-in-browser' };
  if (ios) {
    const browser = iosBrowser(ua);
    if (browser === 'safari') return { kind: 'ios-safari' };
    if (!thirdPartyBrowsersCanAdd(iosVersion(ua))) return { kind: 'ios-old' };
    return { kind: browser === 'chrome' ? 'ios-chrome' : browser === 'edge' ? 'ios-edge' : 'ios-other' };
  }
  return { kind: 'android-manual' };
};

/** Instructions shown when the native dialog is unavailable or was declined. */
export const manualKindFor = (nav = typeof navigator !== 'undefined' ? navigator : { userAgent: '' }) => {
  if (isIos(nav)) return installOption({ nav, standalone: false, state: {}, hasNativePrompt: false })?.kind || 'ios-other';
  return 'android-manual';
};

/** The i18n keys of a panel: steps that exist, in order (see i18n `pwa.menu.kinds.<kind>`). */
export const STEP_KEYS = ['step1', 'step2', 'step3', 'step4'];
